<!--
  CareManagement_QA.vue — Care Management call-quality scorecards.

  THREE LEVELS, master/detail, left-to-right:

    1. CARE MANAGERS   a list — one row per person, ordered by the sort
                       dropdown (highest score first by default)
    2. THEIR CALLS     a table — one row per graded call
    3. ONE CALL        the full rubric: every question, answer, and the
                       verbatim quote the grade was based on

  Scores come from the engagement_qa SAT executor, which grades a call
  transcript against the rubric its TASK TYPE selects (one of six sheets in
  the "VC QA Guide"). Read-only, manager and above — nothing here actions
  anything; an auto-fail is shown to a human with its evidence.

  Presentation follows CareManagement_DischargeWaterfall: cm-toolbar header,
  cm-panel surfaces on #F8F8F9, dense inline-styled tables, tabular numerals,
  #0284C7 for anything selectable.

  IT IS A SAMPLE AND SAYS SO. Only calls WITH a transcript can be graded (~20%
  of engagements, varying by call type), so every level shows its count and
  flags a small sample. A manager must never read a 3-call average as a verdict.
-->
<template>
  <div style="display: flex; flex-direction: column; height: 100%; min-height: 0; background: #F8F8F9;">
    <!-- ── toolbar ────────────────────────────────────────────────────── -->
    <div class="cm-toolbar">
      <h2>Call Quality</h2>

      <span style="font-size: 11.5px; color: #A5A5A6;">
        AI-assisted and human-submitted audits at the individual call level
      </span>

      <div v-if="canAudit" class="cm-seg" style="margin-left: 12px;">
        <button :class="{ on: viewMode === 'report' }" @click="switchToReport">Report</button>
        <button :class="{ on: viewMode === 'audit' }" @click="switchToAudit">Audit calls</button>
      </div>

      <div style="margin-left: auto; display: flex; align-items: center; gap: 10px;">
        <span v-if="!loading && results.length" style="font-size: 11.5px; color: #6B7280; font-variant-numeric: tabular-nums;">
          {{ results.length }} care managers ·
          <span style="font-weight: 600; color: #353537;">{{ totalGraded.toLocaleString() }}</span> graded calls
          <span v-if="totalFlagged" style="color: #B91C1C; font-weight: 600;"> · {{ totalFlagged }} flagged</span>
        </span>
        <button class="cm-icon-btn" :disabled="loading" title="Refresh" @click="load">
          <FontAwesomeIcon :icon="['fas', 'arrows-rotate']" :spin="loading" style="font-size: 11px;" />
        </button>
      </div>
    </div>

    <div style="padding: 0 16px 6px; font-size: 10.5px; color: #A5A5A6; max-width: 900px;">
      Care managers scored against the QA rubric their call type selects. Only calls with a
      transcript can be graded, so these are <em>samples</em> — a score built on a handful of
      calls is marked <span style="color:#B45309; font-weight:600;">small</span> and should be
      read as a prompt to listen, not as a verdict.
    </div>

    <div v-if="viewMode === 'report' && report.audit_count" style="display:flex; gap:8px; padding: 6px 16px 10px; flex-wrap:wrap;">
      <div class="cm-stat"><span class="cm-stat-label">Team average</span><span class="cm-stat-value">{{ fmtPct(report.avg_score_pct) }}</span></div>
      <div class="cm-stat"><span class="cm-stat-label">Audits this month</span><span class="cm-stat-value">{{ report.audit_count }}</span></div>
      <div class="cm-stat is-ok"><span class="cm-stat-label">Green</span><span class="cm-stat-value">{{ fmtPct(report.green_pct) }}</span></div>
      <div class="cm-stat" style="max-width:360px;"><span class="cm-stat-label">Top missed question</span><span style="font-size:11px;font-weight:600;color:#353537;">{{ report.top_missed_question || '—' }}</span></div>
    </div>

    <!-- Manager audit queue: the same QA module and same configured rubrics. -->
    <div v-if="viewMode === 'audit'" style="flex:1;min-height:0;padding:2px 16px 16px;">
      <div class="cm-panel" style="height:100%;display:flex;flex-direction:column;overflow:hidden;">
        <div style="display:flex;align-items:center;gap:8px;padding:10px 14px;border-bottom:1px solid #E8E9E9;">
          <strong style="font-size:12px;color:#353537;">Calls to audit</strong>
          <select
            v-model="auditCmFilter"
            style="font-size:11.5px;padding:4px 8px;border:1px solid #D1D5DB;border-radius:4px;margin-left:12px;"
            @change="auditQueueOffset = 0; loadAuditQueue()"
          >
            <option :value="null">All care managers</option>
            <option v-for="cm in results" :key="cm.care_manager_user_id" class="sensitive" :value="cm.care_manager_user_id">
              {{ cm.care_manager_name }}
            </option>
          </select>
          <input
            v-model="auditSearch"
            class="sensitive"
            type="text"
            placeholder="Search by patient name…"
            style="font-size:11.5px;padding:4px 8px;border:1px solid #D1D5DB;border-radius:4px;width:200px;"
            @input="debouncedSearch"
          />
          <input
            v-model="auditDateFrom"
            type="date"
            style="font-size:11.5px;padding:4px 8px;border:1px solid #D1D5DB;border-radius:4px;width:130px;"
            @change="auditQueueOffset = 0; loadAuditQueue()"
          />
          <span style="font-size:11px;color:#A5A5A6;">to</span>
          <input
            v-model="auditDateTo"
            type="date"
            style="font-size:11.5px;padding:4px 8px;border:1px solid #D1D5DB;border-radius:4px;width:130px;"
            @change="auditQueueOffset = 0; loadAuditQueue()"
          />
          <span style="font-size:10.5px;color:#A5A5A6;margin-left:auto;">
            {{ auditQueueCount }} calls
          </span>
          <button class="cm-icon-btn" @click="auditQueueOffset = 0; loadAuditQueue()">↻</button>
        </div>
        <div style="flex:1;min-height:0;overflow:auto;">
          <table style="width:100%;border-collapse:collapse;">
            <thead><tr><th class="qa-th">Date</th><th class="qa-th">Team member</th><th class="qa-th">Patient / opportunity</th><th class="qa-th">Call type</th><th class="qa-th">Duration</th><th class="qa-th"></th></tr></thead>
            <tbody v-if="auditQueueLoading">
              <tr v-for="n in auditQueueLimit" :key="n" style="border-top:1px solid #F0F0F1;">
                <td class="qa-td"><div class="h-3 w-16 bg-gray-200 rounded animate-pulse" /></td>
                <td class="qa-td"><div class="h-3 w-24 bg-gray-200 rounded animate-pulse" /></td>
                <td class="qa-td"><div class="h-3 w-32 bg-gray-200 rounded animate-pulse mb-1" /><div class="h-2.5 w-16 bg-gray-100 rounded animate-pulse" /></td>
                <td class="qa-td"><div class="h-3 w-20 bg-gray-200 rounded animate-pulse" /></td>
                <td class="qa-td"><div class="h-3 w-10 bg-gray-200 rounded animate-pulse" /></td>
                <td class="qa-td"><div class="h-3 w-12 bg-gray-200 rounded animate-pulse ml-auto" /></td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr v-for="call in auditQueue" :key="call.call_log_uuid" style="border-top:1px solid #F0F0F1;">
                <td class="qa-td">{{ fmtDate(call.engagement_at) }}</td>
                <td class="qa-td sensitive">{{ call.care_manager_name }}</td>
                <td class="qa-td"><div class="sensitive">{{ call.patient_name || 'Unlinked call' }}</div><div style="font-size:10px;color:#A5A5A6;">{{ call.opportunity_type || '—' }}</div></td>
                <td class="qa-td">{{ call.task_type_name || '—' }}</td>
                <td class="qa-td">{{ fmtDuration(call.duration_seconds) }}</td>
                <td class="qa-td" style="text-align:right;"><button class="qa-back" @click="startHumanAudit(call)">{{ call.audit_count ? 'Re-audit' : 'Audit' }}</button></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="auditQueueCount > auditQueueLimit" style="display:flex;align-items:center;justify-content:space-between;padding:8px 14px;border-top:1px solid #E8E9E9;font-size:11.5px;color:#6B7280;">
          <span>{{ auditQueueOffset + 1 }}–{{ Math.min(auditQueueOffset + auditQueueLimit, auditQueueCount) }} of {{ auditQueueCount }}</span>
          <div style="display:flex;gap:6px;">
            <button class="qa-back" :disabled="auditQueueOffset === 0" @click="auditQueueOffset -= auditQueueLimit; loadAuditQueue()">Prev</button>
            <button class="qa-back" :disabled="auditQueueOffset + auditQueueLimit >= auditQueueCount" @click="auditQueueOffset += auditQueueLimit; loadAuditQueue()">Next</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading / empty -->
    <div v-if="loading" class="cm-loading"><div class="cm-spinner"></div></div>

    <div v-else-if="viewMode === 'report' && !results.length" style="flex: 1; display: flex; align-items: center; justify-content: center;">
      <div style="text-align: center; max-width: 320px;">
        <div style="font-size: 12px; font-weight: 700; color: #353537;">No graded calls yet</div>
        <div style="font-size: 10.5px; color: #A5A5A6; margin-top: 4px; line-height: 1.5;">
          Grading needs a transcript. Check that call recording is flowing for these
          task types.
        </div>
      </div>
    </div>

    <!-- ── three levels ───────────────────────────────────────────────── -->
    <div v-else-if="viewMode === 'report'" style="flex: 1; min-height: 0; display: flex; gap: 12px; padding: 2px 16px 16px;">
      <!-- 1. CARE MANAGERS -->
      <div class="cm-panel" style="width: 340px; flex: 0 0 340px; display: flex; flex-direction: column; min-height: 0; overflow: hidden;">
        <div style="display: flex; align-items: baseline; gap: 8px; padding: 11px 14px 9px; border-bottom: 1px solid #E8E9E9;">
          <div style="font-size: 12px; font-weight: 700; color: #353537;">Care Managers</div>
          <select
            v-model="sortBy"
            class="qa-sort"
            style="margin-left: auto;"
            aria-label="Sort care managers"
          >
            <option value="score_desc">Highest score first</option>
            <option value="score_asc">Lowest score first</option>
            <option value="flagged_desc">Most flagged first</option>
            <option value="calls_desc">Most calls first</option>
            <option value="name_asc">Name (A–Z)</option>
          </select>
        </div>

        <div style="flex: 1; min-height: 0; overflow-y: auto;">
          <div
            v-for="cm in rankedResults"
            :key="cm.care_manager_user_id"
            class="qa-cm-row"
            :class="{ on: selectedCm === cm.care_manager_user_id }"
            @click="selectCm(cm)"
          >
            <div style="display: flex; align-items: baseline; gap: 8px;">
              <div class="sensitive" style="font-size: 11.5px; font-weight: 600; color: #353537; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                {{ cm.care_manager_name || `User ${cm.care_manager_user_id}` }}
              </div>
              <div
                style="margin-left: auto; font-size: 15px; font-weight: 700; font-variant-numeric: tabular-nums; line-height: 1;"
                :style="{ color: scoreColour(cm.overall.avg_score_pct) }"
              >
                {{ fmtPct(cm.overall.avg_score_pct) }}
              </div>
            </div>

            <!-- The bar is the ranking, read at a glance; the number is the value. -->
            <div style="height: 3px; border-radius: 2px; background: #F0F0F1; margin: 6px 0 5px; overflow: hidden;">
              <div
                style="height: 100%; border-radius: 2px;"
                :style="{ width: barWidth(cm.overall.avg_score_pct), background: scoreColour(cm.overall.avg_score_pct) }"
              ></div>
            </div>

            <div style="display: flex; align-items: center; gap: 8px; font-size: 10px; color: #A5A5A6; font-variant-numeric: tabular-nums;">
              <span>{{ cm.overall.graded_count }} graded</span>
              <span v-if="cm.overall.failed_count" style="color: #B91C1C; font-weight: 600;">
                {{ cm.overall.failed_count }} flagged
              </span>
              <span v-if="cm.overall.small_sample" class="qa-tag qa-tag-warn" style="margin-left: auto;">small</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 2. CALLS + 3. ONE CALL -->
      <div class="cm-panel" style="flex: 1; min-width: 0; display: flex; flex-direction: column; min-height: 0; overflow: hidden;">
        <div v-if="!activeCm" style="flex: 1; display: flex; align-items: center; justify-content: center;">
          <div style="text-align: center;">
            <div style="font-size: 12px; font-weight: 600; color: #6B7280;">Select a care manager</div>
            <div style="font-size: 10.5px; color: #A5A5A6; margin-top: 3px;">
              their graded calls, then the rubric behind any one of them
            </div>
          </div>
        </div>

        <template v-else>
          <!-- Breadcrumb: the two levels below the list, always showing where you are. -->
          <div style="display: flex; align-items: center; gap: 8px; padding: 10px 14px; border-bottom: 1px solid #E8E9E9; flex-wrap: wrap;">
            <div class="qa-crumb sensitive" :class="{ on: !activeCall }" @click="activeCall = null">
              {{ activeCm.care_manager_name || `User ${activeCm.care_manager_user_id}` }}
            </div>
            <template v-if="activeCall">
              <FontAwesomeIcon :icon="['fas', 'angle-right']" style="font-size: 10px; color: #D4D4D6;" />
              <div class="qa-crumb on">
                {{ activeCall.rubric_label }} · {{ fmtDate(activeCall.engagement_at) }}
              </div>
            </template>
            <span v-else style="font-size: 10.5px; color: #A5A5A6; font-variant-numeric: tabular-nums;">
              {{ activeCalls.length }} graded {{ activeCalls.length === 1 ? 'call' : 'calls' }}
              <span v-if="activeCm.overall.small_sample" style="color: #B45309;">
                — small sample, read individual calls rather than the average
              </span>
            </span>
            <button
              v-if="activeCall"
              class="qa-back"
              style="margin-left: auto;"
              @click="activeCall = null"
            >
              <FontAwesomeIcon :icon="['fas', 'chevron-left']" style="font-size: 9px;" />
              All calls
            </button>
          </div>

          <!-- 2. that CM's calls -->
          <div v-if="!activeCall" style="flex: 1; min-height: 0; overflow: auto;">
            <table style="width: 100%; border-collapse: collapse;">
              <thead>
                <tr>
                  <th class="qa-th" style="width: 108px;">Date</th>
                  <th class="qa-th">Rubric</th>
                  <th class="qa-th">Call type</th>
                  <th class="qa-th" style="text-align: right; width: 92px;">Points</th>
                  <th class="qa-th" style="text-align: right; width: 96px;">Score</th>
                  <th class="qa-th" style="width: 24px;"></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="c in activeCalls"
                  :key="c.work_id"
                  class="qa-call-row"
                  style="border-top: 1px solid #F0F0F1;"
                  @click="activeCall = c"
                >
                  <td class="qa-td" style="color: #6B7280; white-space: nowrap;">{{ fmtDate(c.engagement_at) }}</td>
                  <td class="qa-td" style="font-weight: 500;">
                    {{ c.rubric_label }}
                    <span v-if="c.failed" class="qa-tag qa-tag-fail" style="margin-left: 6px;">auto-fail</span>
                  </td>
                  <td class="qa-td qa-td-clip" :title="c.task_type_name || ''" style="color: #6B7280;">
                    {{ c.task_type_name || '—' }}
                  </td>
                  <td class="qa-td" style="text-align: right; color: #A5A5A6; font-variant-numeric: tabular-nums;">
                    {{ c.earned }}/{{ c.applicable }}
                  </td>
                  <td
                    class="qa-td"
                    style="text-align: right; font-size: 13px; font-weight: 700; font-variant-numeric: tabular-nums;"
                    :style="{ color: scoreColour(c.score_pct) }"
                  >
                    {{ fmtPct(c.score_pct) }}
                  </td>
                  <td class="qa-td">
                    <FontAwesomeIcon
                      :icon="['fas', 'chevron-right']"
                      class="qa-chev"
                      style="font-size: 9px;"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- 3. one call: the full rubric -->
          <div v-else style="flex: 1; min-height: 0; overflow: auto;">
            <div v-if="callLoading" class="cm-loading" style="padding: 40px 0;"><div class="cm-spinner"></div></div>

            <template v-else>
              <!-- The verdict, its arithmetic, and what a coach would say — before
                   the question-by-question detail, which is the evidence for it. -->
              <div style="padding: 14px 16px; border-bottom: 1px solid #E8E9E9; background: #FCFCFD;">
                <div style="display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap;">
                  <span
                    style="font-size: 26px; font-weight: 700; line-height: 1; font-variant-numeric: tabular-nums;"
                    :style="{ color: scoreColour(callDetail.score_pct) }"
                  >{{ fmtPct(callDetail.score_pct) }}</span>
                  <span style="font-size: 11px; color: #6B7280; font-variant-numeric: tabular-nums;">
                    {{ callDetail.earned }} of {{ callDetail.applicable }} applicable points
                  </span>
                  <span v-if="callDetail.failed" class="qa-tag qa-tag-fail">auto-fail</span>
                  <span style="margin-left: auto; font-size: 10.5px; color: #A5A5A6;">
                    {{ answerTally }}
                  </span>
                </div>

                <p v-if="callDetail.summary" style="font-size: 11.5px; color: #353537; line-height: 1.55; margin: 10px 0 0; max-width: 860px;">
                  {{ callDetail.summary }}
                </p>

                <div v-if="callDetail.coaching" style="display: flex; gap: 8px; margin-top: 9px; max-width: 860px; padding: 8px 10px; border: 1px solid #E9D5FF; border-radius: 6px; background: #FAF5FF;">
                  <div style="font-size: 10px; font-weight: 700; color: #7C3AED; text-transform: uppercase; letter-spacing: .03em; white-space: nowrap; padding-top: 1px;">Coaching</div>
                  <div style="font-size: 11.5px; color: #4C1D95; line-height: 1.5;">{{ callDetail.coaching }}</div>
                </div>
              </div>

              <table style="width: 100%; border-collapse: collapse;">
                <thead>
                  <tr>
                    <th class="qa-th" style="width: 66px;">Answer</th>
                    <th class="qa-th">Question</th>
                    <th class="qa-th" style="width: 42%;">Evidence from the transcript</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(a, i) in (callDetail.answers || [])"
                    :key="i"
                    style="border-top: 1px solid #F0F0F1; vertical-align: top;"
                    :style="isAutoFail(a) ? { background: '#FEF6F6' } : null"
                  >
                    <td class="qa-td" style="padding-top: 9px;">
                      <span class="qa-tag" :style="answerStyle(a.answer)">{{ a.answer }}</span>
                    </td>
                    <td class="qa-td" style="padding-top: 9px; padding-bottom: 9px; line-height: 1.45;">
                      {{ questionText(a.id) }}
                      <span v-if="isAutoFail(a)" style="margin-left: 5px; font-size: 9.5px; font-weight: 700; color: #B91C1C; letter-spacing: .03em;">AUTO-FAIL</span>
                      <div v-if="a.rationale" style="font-size: 10.5px; color: #A5A5A6; margin-top: 3px; line-height: 1.45;">
                        {{ a.rationale }}
                      </div>
                    </td>
                    <td class="qa-td" style="padding-top: 9px; padding-bottom: 9px;">
                      <!-- The quote is the whole point of the level: a grade a manager
                           can check against what was actually said. -->
                      <div v-if="a.quote" style="border-left: 2px solid #E8E9E9; padding-left: 9px; font-size: 10.5px; color: #6B7280; font-style: italic; line-height: 1.5;">
                        “{{ a.quote }}”
                      </div>
                      <span v-else style="color: #D4D4D6; font-size: 10.5px;">no quote captured</span>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div v-if="!(callDetail.answers || []).length" style="padding: 28px; text-align: center; color: #A5A5A6; font-size: 11.5px;">
                No rubric answers were returned for this call.
              </div>
            </template>
          </div>
        </template>
      </div>
    </div>

    <teleport to="body">
      <div v-if="humanAuditCall" class="fixed inset-0 z-50 flex items-center justify-center bg-black/40" @click.self="closeHumanAudit">
        <div class="bg-white rounded-lg border border-gray-200 w-[60rem] max-w-[95vw] max-h-[92vh] flex flex-col">
          <div class="px-4 py-3 border-b border-gray-100 flex items-center"><div><h3 class="text-sm font-semibold text-gray-900">{{ humanAuditCall.audit_count ? 'Re-audit' : 'Audit' }} call</h3><p class="text-xs text-gray-500 sensitive">{{ humanAuditCall.care_manager_name }} · {{ humanAuditCall.patient_name }}</p></div><button class="ml-auto text-gray-400" @click="closeHumanAudit">×</button></div>
          <div class="p-4 overflow-y-auto">
            <div v-if="humanAuditCall.has_recordings" style="margin-bottom:12px;padding:10px;border:1px solid #E8E9E9;border-radius:6px;background:#F8F8F9;">
              <div style="font-size:10.5px;font-weight:600;color:#6B7280;margin-bottom:6px;">Call recording</div>
              <audio v-if="humanRecordingUrl" :src="humanRecordingUrl" controls style="width:100%;height:32px;"></audio>
              <button v-else class="qa-back" :disabled="humanRecordingLoading" @click="loadHumanRecording">
                {{ humanRecordingLoading ? 'Preparing audio…' : 'Play audio' }}
              </button>
            </div>
            <label class="block text-xs font-medium text-gray-600 mb-1">Audit type</label>
            <select v-model.number="humanAudit.template_id" class="qa-sort" style="max-width:none;width:100%;padding:6px;margin-bottom:12px;" @change="resetHumanAnswers"><option :value="null">Select a team rubric…</option><option v-for="r in rubrics" :key="r.id" :value="r.id">{{ r.rubric_label }}</option></select>
            <div v-if="humanAuditCall.audit_count" style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:12px;"><select v-model="humanAudit.re_audit_reason" class="qa-sort" style="max-width:none;padding:6px;"><option value="">Re-audit reason…</option><option value="new_information">New information discovered</option><option value="qa_dispute">QA dispute</option><option value="other">Other</option></select><input v-model="humanAudit.re_audit_note" class="qa-sort" style="max-width:none;padding:6px;" placeholder="Optional note" /></div>
            <div v-for="(q,i) in humanQuestions" :key="q.id" style="padding:10px 0;border-top:1px solid #F0F0F1;"><div style="font-size:11.5px;color:#353537;margin-bottom:7px;">{{ q.question }}</div><div style="display:flex;gap:6px;"><button v-for="answer in ['Yes','No','N/A']" :key="answer" class="qa-back" :style="humanAudit.answers[i]?.answer === answer ? {borderColor:'#F76734',color:'#F76734',background:'#FEF3F0'} : {}" @click="humanAudit.answers[i].answer=answer">{{ answer }}</button></div></div>
            <p v-if="humanAuditError" style="font-size:11px;color:#B91C1C;margin-top:8px;">{{ humanAuditError }}</p>
          </div>
          <div class="px-4 py-3 border-t border-gray-100 flex justify-end gap-2"><button class="qa-back" @click="closeHumanAudit">Cancel</button><button class="cm-btn cm-btn-primary cm-btn-sm" :disabled="!humanAuditComplete || humanAuditSaving" @click="submitHumanAudit">{{ humanAuditSaving ? 'Submitting…' : 'Submit audit' }}</button></div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import debounce from 'lodash/debounce';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { mapGetters } from 'vuex';
import { listScorecards, getReport, listRubrics, listAuditQueue, listCalls, submitAudit } from '@/services/api/cmQaApi';
import { getJobStatus } from '@/services/api/jobsApi';
import { getCallAudio, prepareCallAudio } from '@/services/api/voiceApi';

export default {
  name: 'CareManagementQA',
  components: { FontAwesomeIcon },
  props: {
    initialMode: { type: String, default: 'report' },
    initialCmUserId: { type: Number, default: null },
    initialSessionId: { type: String, default: null },
  },
  data() {
    return {
      loading: false,
      callLoading: false,
      sortBy: 'score_desc',
      results: [],
      selectedCm: null,
      activeCm: null,
      activeCalls: [],
      activeCall: null,
      callDetail: {},
      viewMode: this.initialMode,
      report: {},
      auditQueue: [],
      auditQueueCount: 0,
      auditQueueOffset: 0,
      auditQueueLimit: 20,
      auditQueueLoading: false,
      auditSearch: '',
      auditCmFilter: null,
      auditDateFrom: '',
      auditDateTo: '',
      rubrics: [],
      humanAuditCall: null,
      humanAudit: { template_id: null, answers: [], re_audit_reason: '', re_audit_note: '' },
      humanAuditSaving: false,
      humanAuditError: '',
      humanRecordingUrl: '',
      humanRecordingLoading: false,
      _audioPollTimer: null,
    };
  },
  computed: {
    ...mapGetters('auth', ['current_user']),
    ...mapGetters('tenant', ['isManagerViewer', 'isTenantAdmin']),
    canAudit() { return this.isManagerViewer || this.isTenantAdmin || !!this.current_user?.is_superuser; },
    activeHumanRubric() { return this.rubrics.find(r => r.id === this.humanAudit.template_id); },
    humanQuestions() { return this.activeHumanRubric?.questions || []; },
    humanAuditComplete() {
      return !!this.humanQuestions.length
        && this.humanAudit.answers.length === this.humanQuestions.length
        && this.humanAudit.answers.every(a => a.answer)
        && (!this.humanAuditCall?.audit_count || this.humanAudit.re_audit_reason);
    },
    // User-selectable ordering. In EVERY score-based order a null score sorts
    // LAST, never first — an unscored care manager is a data gap, not a top or
    // bottom performer, and must not head the list either way.
    rankedResults() {
      const rows = [...this.results];
      const score = cm => {
        const s = cm.overall.avg_score_pct;
        return s === null || s === undefined ? null : s;
      };
      const byScore = dir => (a, b) => {
        const x = score(a);
        const y = score(b);
        if (x === null && y === null) return 0;
        if (x === null) return 1;
        if (y === null) return -1;
        return dir * (x - y);
      };
      switch (this.sortBy) {
        case 'score_asc':
          return rows.sort(byScore(1));
        case 'flagged_desc':
          return rows.sort(
            (a, b) => (b.overall.failed_count || 0) - (a.overall.failed_count || 0),
          );
        case 'calls_desc':
          return rows.sort(
            (a, b) => (b.overall.graded_count || 0) - (a.overall.graded_count || 0),
          );
        case 'name_asc':
          return rows.sort((a, b) =>
            String(a.care_manager_name || '').localeCompare(String(b.care_manager_name || '')),
          );
        case 'score_desc':
        default:
          return rows.sort(byScore(-1));
      }
    },
    totalGraded() {
      return this.results.reduce((n, cm) => n + (cm.overall.graded_count || 0), 0);
    },
    totalFlagged() {
      return this.results.reduce((n, cm) => n + (cm.overall.failed_count || 0), 0);
    },
    // The per-question TEXT lives on the SAT rubric, not on the answer rows, so
    // the detail view maps id -> question from whatever the API supplied.
    questionMap() {
      const m = {};
      for (const f of this.callDetail.auto_fails || []) m[f.id] = f.question;
      for (const q of this.callDetail.questions || []) m[q.id] = q.question;
      return m;
    },
    // Yes / No / N-A counts, so the header says how the score was composed
    // without the reader tallying pills.
    answerTally() {
      const answers = this.callDetail.answers || [];
      if (!answers.length) return '';
      const n = { Yes: 0, No: 0, other: 0 };
      answers.forEach(a => {
        if (a.answer === 'Yes') n.Yes += 1;
        else if (a.answer === 'No') n.No += 1;
        else n.other += 1;
      });
      const parts = [`${n.Yes} yes`, `${n.No} no`];
      if (n.other) parts.push(`${n.other} n/a`);
      return parts.join(' · ');
    },
  },
  created() {
    this.debouncedSearch = debounce(() => {
      this.auditQueueOffset = 0;
      this.loadAuditQueue();
    }, 300);
  },
  async mounted() {
    await this.load();
    if (this.viewMode === 'audit') {
      await this.loadAuditQueue();
      if (this.initialSessionId) {
        const call = this.auditQueue.find((c) => c.call_log_uuid === this.initialSessionId);
        if (call) this.startHumanAudit(call, { updateUrl: false });
      }
    }
  },
  beforeUnmount() {
    if (this.humanRecordingUrl) URL.revokeObjectURL(this.humanRecordingUrl);
    if (this._audioPollTimer) clearTimeout(this._audioPollTimer);
    if (this.debouncedSearch) this.debouncedSearch.cancel();
  },
  methods: {
    async load() {
      this.loading = true;
      this.selectedCm = null;
      this.activeCm = null;
      this.activeCall = null;
      try {
        const [scorecards, report, rubrics] = await Promise.all([
          listScorecards(),
          getReport(),
          listRubrics(),
        ]);
        this.results = scorecards.results || [];
        this.report = report || {};
        this.rubrics = rubrics.results || [];
      } catch (e) {
        console.error('Failed to load QA scorecards:', e);
        this.results = [];
      } finally {
        this.loading = false;
      }
      if (this.initialCmUserId && this.results.length) {
        const cm = this.results.find((r) => r.care_manager_user_id === this.initialCmUserId);
        if (cm) this.selectCm(cm, { updateUrl: false });
      }
    },

    switchToReport() {
      this.viewMode = 'report';
      this.$router.replace('/caremanagement/qa/report').catch(() => {});
    },
    async switchToAudit() {
      this.viewMode = 'audit';
      this.$router.replace('/caremanagement/qa/audit-calls').catch(() => {});
      if (!this.auditQueue.length) {
        await this.loadAuditQueue();
        if (this.initialSessionId) {
          const call = this.auditQueue.find((c) => c.call_log_uuid === this.initialSessionId);
          if (call) this.startHumanAudit(call, { updateUrl: false });
        }
      }
    },
    async loadAuditQueue() {
      this.auditQueueLoading = true
      try {
        const filters = {}
        if (this.auditSearch) filters.search = this.auditSearch
        if (this.auditCmFilter) filters.care_manager_user_id = this.auditCmFilter
        if (this.auditDateFrom) filters.date_from = this.auditDateFrom
        if (this.auditDateTo) filters.date_to = this.auditDateTo
        const data = await listAuditQueue(filters, {
          limit: this.auditQueueLimit,
          offset: this.auditQueueOffset,
        })
        this.auditQueue = data.results || []
        this.auditQueueCount = data.count || 0
      } finally {
        this.auditQueueLoading = false
      }
    },
    startHumanAudit(call, { updateUrl = true } = {}) {
      if (this._audioPollTimer) clearTimeout(this._audioPollTimer);
      this._audioPollTimer = null;
      this.humanAuditCall = call;
      this.humanAuditError = '';
      this.humanRecordingLoading = false;
      if (this.humanRecordingUrl) URL.revokeObjectURL(this.humanRecordingUrl);
      this.humanRecordingUrl = '';
      this.humanAudit = { template_id: this.rubrics[0]?.id || null, answers: [], re_audit_reason: '', re_audit_note: '' };
      this.resetHumanAnswers();
      if (updateUrl) {
        this.$router.push(`/caremanagement/qa/audit-calls/${call.call_log_uuid || call.uuid}`).catch(() => {});
      }
    },
    closeHumanAudit() {
      if (this._audioPollTimer) clearTimeout(this._audioPollTimer);
      this._audioPollTimer = null;
      if (this.humanRecordingUrl) URL.revokeObjectURL(this.humanRecordingUrl);
      this.humanRecordingUrl = '';
      this.humanRecordingLoading = false;
      this.humanAuditCall = null;
      this.$router.push('/caremanagement/qa/audit-calls').catch(() => {});
    },
    resetHumanAnswers() {
      this.humanAudit.answers = this.humanQuestions.map(q => ({ id: q.id, answer: '', quote: '', rationale: '' }));
    },
    async loadHumanRecording() {
      this.humanRecordingLoading = true;
      try {
        const uuid = this.humanAuditCall.call_log_uuid;

        if (this.humanAuditCall.call_audio_url) {
          const blob = await getCallAudio(uuid);
          this.humanRecordingUrl = URL.createObjectURL(blob);
          return;
        }

        const result = await prepareCallAudio(uuid);
        if (result.call_audio_url) {
          const blob = await getCallAudio(uuid);
          this.humanRecordingUrl = URL.createObjectURL(blob);
          return;
        }
        if (result.job_id) {
          await this._pollJobUntilReady(uuid, result.job_id);
          return;
        }

        throw new Error('No recording is available.');
      } catch (e) {
        this.humanAuditError = e?.response?.data?.detail || e.message || 'Could not load the recording.';
      } finally {
        this.humanRecordingLoading = false;
      }
    },
    async _pollJobUntilReady(uuid, jobId) {
      const expectedCall = this.humanAuditCall;
      const poll = async (resolve, reject) => {
        // Demo Mode starting mid-poll ends the poll; the recording is not loaded.
        if (this.humanAuditCall !== expectedCall) return resolve();
        try {
          const data = await getJobStatus(jobId);
          if (data.status === 'completed') {
            if (this.humanAuditCall !== expectedCall) return resolve();
            const blob = await getCallAudio(uuid);
            this.humanRecordingUrl = URL.createObjectURL(blob);
            resolve();
          } else if (data.status === 'failed') {
            reject(new Error(data.error_message || 'Failed to prepare recording'));
          } else {
            this._audioPollTimer = setTimeout(() => poll(resolve, reject), 2000);
          }
        } catch (e) {
          reject(e);
        }
      };
      return new Promise(poll);
    },
    async submitHumanAudit() {
      this.humanAuditSaving = true;
      this.humanAuditError = '';
      try {
        let original = null;
        if (this.humanAuditCall.audit_count) {
          const calls = await listCalls(this.humanAuditCall.care_manager_user_id);
          original = (calls.results || []).find(c => c.call_log_uuid === this.humanAuditCall.call_log_uuid && c.source === 'human');
        }
        await submitAudit({
          call_log_uuid: this.humanAuditCall.call_log_uuid,
          template_id: this.humanAudit.template_id,
          answers: this.humanAudit.answers,
          re_audit_of_id: original?.analyst_job_id || null,
          re_audit_reason: this.humanAudit.re_audit_reason,
          re_audit_note: this.humanAudit.re_audit_note,
        });
        this.humanAuditCall = null;
        await Promise.all([this.load(), this.loadAuditQueue()]);
      } catch (e) {
        this.humanAuditError = e?.response?.data?.detail || 'Could not submit audit.';
      } finally {
        this.humanAuditSaving = false;
      }
    },
    fmtDuration(seconds) {
      if (!seconds) return '—';
      return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
    },

    // Level 1 -> 2. The scorecard payload already carries each rubric's calls,
    // so flattening them is enough — no second request for the list itself.
    selectCm(cm, { updateUrl = true } = {}) {
      this.selectedCm = cm.care_manager_user_id;
      this.activeCm = cm;
      this.activeCall = null;
      const calls = [];
      for (const r of cm.rubrics || []) {
        for (const c of r.calls || []) {
          calls.push({ ...c, rubric_label: c.rubric_label || r.rubric_label });
        }
      }
      calls.sort((a, b) => String(b.engagement_at || '').localeCompare(String(a.engagement_at || '')));
      this.activeCalls = calls;
      if (updateUrl) {
        this.$router.push(`/caremanagement/qa/report/${cm.care_manager_user_id}`).catch(() => {});
      }
    },

    fmtPct(v) {
      return v === null || v === undefined ? '—' : `${v}%`;
    },
    fmtDate(v) {
      if (!v) return '';
      const d = new Date(v);
      return Number.isNaN(d.getTime())
        ? String(v).slice(0, 10)
        : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
    // Coarse bands on purpose: these are samples of a handful of calls, so a
    // fine-grained colour ramp would imply more precision than the data has.
    scoreColour(v) {
      if (v === null || v === undefined) return '#A5A5A6';
      if (v >= 85) return '#0E6B43';
      if (v >= 70) return '#353537';
      return '#B45309';
    },
    barWidth(v) {
      if (v === null || v === undefined) return '0%';
      return `${Math.max(0, Math.min(100, v))}%`;
    },
    answerStyle(a) {
      if (a === 'Yes') return { background: '#ECFDF5', color: '#0E6B43', borderColor: '#A7F3D0' };
      if (a === 'No') return { background: '#FEF2F2', color: '#B91C1C', borderColor: '#FECACA' };
      return { background: '#F8F8F9', color: '#A5A5A6', borderColor: '#E8E9E9' };
    },
    isAutoFail(a) {
      return (this.callDetail.auto_fails || []).some(f => f.id === a.id);
    },
    questionText(id) {
      const af = (this.callDetail.auto_fails || []).find(f => f.id === id);
      if (af) return af.question;
      return this.questionMap[id] || id;
    },
  },
  watch: {
    initialMode(val) {
      this.viewMode = val;
      if (val === 'audit' && !this.auditQueue.length) this.loadAuditQueue();
    },
    initialSessionId(val) {
      if (val && this.auditQueue.length) {
        const call = this.auditQueue.find((c) => c.call_log_uuid === val);
        if (call) this.startHumanAudit(call, { updateUrl: false });
      } else if (!val && this.humanAuditCall) {
        this.humanAuditCall = null;
      }
    },
    initialCmUserId(val) {
      if (val && this.results.length) {
        const cm = this.results.find((r) => r.care_manager_user_id === val);
        if (cm && this.selectedCm !== val) this.selectCm(cm, { updateUrl: false });
      } else if (!val) {
        this.selectedCm = null;
        this.activeCm = null;
        this.activeCall = null;
        this.activeCalls = [];
      }
    },
    async activeCall(c) {
      if (!c) { this.callDetail = {}; return; }
      this.callLoading = true;
      try {
        const calls = await listCalls(this.selectedCm);
        const match = (calls.results || []).find(r => String(r.work_id) === String(c.work_id));
        this.callDetail = match || c;
      } catch (e) {
        console.error('Failed to load call detail:', e);
        this.callDetail = c;
      } finally {
        this.callLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Level 1 rows. A card-per-person rather than a four-column table: the name and
   the score are the only things scanned, and the bar carries the ranking the
   sort already implies. */
/* Call rows are navigation — they must look it. wf-clickable-row was borrowed
   from CareManagement_DischargeWaterfall.vue, but that file's <style> is
   SCOPED, so the class never reached this component and the rows had no
   pointer and no hover. Same for the back button (wf-copy). Both are defined
   locally here instead of depending on another component's scoped styles. */
.qa-call-row { cursor: pointer; }
.qa-call-row:hover { background: #F8FAFC; }
.qa-call-row:hover .qa-chev { color: #0284C7; transform: translateX(1px); }
.qa-chev { color: #D4D4D6; transition: color .12s ease, transform .12s ease; }

.qa-back {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border: 1px solid #E8E9E9;
  border-radius: 4px;
  background: #FFFFFF;
  color: #6B7280;
  font-size: 10.5px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.qa-back:hover { border-color: #0284C7; color: #0369A1; background: #F8FAFC; }

.qa-sort {
  font-size: 10.5px;
  color: #57575A;
  background: #FFFFFF;
  border: 1px solid #E8E9E9;
  border-radius: 4px;
  padding: 2px 4px;
  cursor: pointer;
  max-width: 155px;
}
.qa-sort:hover { border-color: #CFD1D2; }
.qa-sort:focus { outline: none; border-color: #0284C7; }

.qa-cm-row {
  padding: 9px 14px;
  border-bottom: 1px solid #F0F0F1;
  border-left: 2px solid transparent;
  cursor: pointer;
}
.qa-cm-row:hover { background: #F8FAFC; }
.qa-cm-row.on { background: #F0F9FF; border-left-color: #0284C7; }

/* Breadcrumb between levels 2 and 3. */
.qa-crumb {
  font-size: 11.5px;
  font-weight: 600;
  color: #6B7280;
  cursor: pointer;
}
.qa-crumb:hover { color: #0369A1; }
.qa-crumb.on { color: #353537; cursor: default; }

.qa-th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #FFFFFF;
  border-bottom: 1px solid #E8E9E9;
  text-align: left;
  padding: 7px 10px;
  font-size: 10.5px;
  font-weight: 600;
  color: #6B7280;
  white-space: nowrap;
}
.qa-td {
  padding: 7px 10px;
  font-size: 11.5px;
  color: #353537;
}
.qa-td-clip {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Small status pills: answers, auto-fail, small sample. */
.qa-tag {
  display: inline-block;
  padding: 1px 6px;
  border: 1px solid #E8E9E9;
  border-radius: 9px;
  font-size: 9.5px;
  font-weight: 600;
  line-height: 15px;
  white-space: nowrap;
}
.qa-tag-warn { background: #FFFBEB; color: #B45309; border-color: #FDE68A; }
.qa-tag-fail { background: #FEF2F2; color: #B91C1C; border-color: #FECACA; }
</style>
